// One in-flight batch on the Simulations active strip: name, status badge,
// capacity-wait label (from the generated `is_waiting_for_capacity` field
// only; docs/09 §4), and a live progress bar. Progress counts come from ONE
// `POST /v1/metrics:query` per active batch — `transcript_volume` grouped by
// `transcript_status` filtered to `batch_id` (a registry-approved no-window
// filter, so no time_range) — never inferred from elapsed time. SSE
// `batch.updated` invalidates `queryKeys.batches`, which re-renders this card;
// the count query refetches on an interval only while the batch is active.
import { useQuery } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { EMPTY_METRIC_FILTERS } from "../../lib/dashboardData";
import { ApiRequestError } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { Badge, Button, Inline, Progress, Stack, Text } from "../../ui";
import { CapacityWaitStatus } from "../CapacityWaitStatus";
import { batchDisplayLabel, scheduledTemplateLabel } from "./batchPresentation";

type BatchResponse = components["schemas"]["BatchResponse"];
type MetricsQueryResponse = components["schemas"]["MetricsQueryResponse"];

const ACTIVE_PROGRESS_REFETCH_MS = 10_000;

export interface BatchProgressCounts {
  settled: number;
  total: number;
}

// Sum the grouped `transcript_status` counts: settled = ready + failed,
// total = every status (all rows exist up front, so this is the cell count).
export function batchProgressCounts(
  response: MetricsQueryResponse,
): BatchProgressCounts {
  let settled = 0;
  let total = 0;
  for (const series of response.series ?? []) {
    for (const group of series.groups ?? []) {
      const status = group.dimensions?.find(
        (dimension) => dimension.dimension === "transcript_status",
      )?.value;
      for (const point of group.points ?? []) {
        if (point.kind !== "scalar") {
          continue;
        }
        total += point.value;
        if (status === "ready" || status === "failed") {
          settled += point.value;
        }
      }
    }
  }
  return { settled, total };
}

export interface ActiveSimulationCardProps {
  batch: BatchResponse;
  isHighlighted: boolean;
  onOpenInTranscripts: (batchId: string) => void;
  templateNamesById: ReadonlyMap<string, string>;
}

export function ActiveSimulationCard({
  batch,
  isHighlighted,
  onOpenInTranscripts,
  templateNamesById,
}: ActiveSimulationCardProps) {
  const progress = useQuery({
    queryKey: queryKeys.metrics.list({
      batch_id: batch.id,
      surface: "simulations-active-card",
    }),
    queryFn: async () => {
      const { data, error } = await apiClient.POST("/v1/metrics:query", {
        body: {
          aggregate: "default",
          bucket: "none",
          filters: { ...EMPTY_METRIC_FILTERS, batch_id: batch.id },
          group_by: ["transcript_status"],
          metric: "transcript_volume",
          time_range: null,
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    refetchInterval: ACTIVE_PROGRESS_REFETCH_MS,
  });

  const counts =
    progress.data === undefined
      ? undefined
      : batchProgressCounts(progress.data);

  return (
    <ActiveSimulationCardView
      batch={batch}
      counts={counts}
      isHighlighted={isHighlighted}
      onOpenInTranscripts={onOpenInTranscripts}
      templateNamesById={templateNamesById}
    />
  );
}

export interface ActiveSimulationCardViewProps {
  batch: BatchResponse;
  counts?: BatchProgressCounts;
  isHighlighted: boolean;
  onOpenInTranscripts: (batchId: string) => void;
  templateNamesById: ReadonlyMap<string, string>;
}

export function ActiveSimulationCardView({
  batch,
  counts,
  isHighlighted,
  onOpenInTranscripts,
  templateNamesById,
}: ActiveSimulationCardViewProps) {
  const label = batchDisplayLabel(batch);
  const scheduledTemplate = scheduledTemplateLabel(batch, templateNamesById);
  return (
    <Stack
      className={
        isHighlighted
          ? "marker-active-simulation marker-active-simulation--highlighted"
          : "marker-active-simulation"
      }
      gap={2}
    >
      <Inline align="center" gap={3}>
        <Text>{label}</Text>
        {scheduledTemplate === null ? null : (
          <Badge tone="neutral">{scheduledTemplate}</Badge>
        )}
        <Badge tone={batch.status === "running" ? "success" : "neutral"}>
          {batch.status}
        </Badge>
        <CapacityWaitStatus
          isWaitingForCapacity={batch.is_waiting_for_capacity}
          capacityWaitCause={batch.capacity_wait_cause}
        />
        {isHighlighted ? <Badge tone="success">just launched</Badge> : null}
        <Button
          size="sm"
          type="button"
          variant="secondary"
          onClick={() => {
            onOpenInTranscripts(batch.id);
          }}
        >
          View in Transcripts
        </Button>
      </Inline>
      {counts !== undefined && counts.total > 0 ? (
        <Stack gap={1}>
          <Progress
            label={`${label} progress`}
            max={counts.total}
            value={counts.settled}
          />
          <Text variant="muted">
            {counts.settled} of {counts.total} conversations settled
          </Text>
        </Stack>
      ) : (
        <Text variant="muted">Setting up conversations…</Text>
      )}
    </Stack>
  );
}
